import { createElement, isHTMLElement } from "../../../core/js/utilities";
import { actions, dispatchEvent, renderState } from "../../../core/js/state-machine/index";
import {
    componentName,
    componentClasses,
    defaultProps,
    eventMap,
    views,
    schema,
    icons,
} from "../../../core/components/notification/notification";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import notificationTemplate from "./notification.template";

export const validateHTML = isHTMLElement;

/**
 * Creates Notification
 * @param {Object} props - Appearance and Content props to create and append a new Notification
 * @param {HTMLElement} node - The designated parent element to add the Notification wrapper
 * @returns Notification
 */
export default function Notification(props, element = document.body) {
    const [success, mergedProps] = validateInitialization({
        componentName,
        element,
        defaultProps,
        props,
        views,
        schema,
        validateHTML,
    });
    if (!success) return;

    /**
     * Builds a wrapper for children notification
     * and append it to the parent element.
     * @private
     */
    const buildWrapper = () => {
        const newWrapper = createElement("div", {
            class: componentClasses.wrapper,
            "data-dds": "notification",
        });
        element.append(newWrapper);
        notificationWrapper = newWrapper;
    };

    /**
     * Builds a new notification, append to the wrapper and show it.
     * @private
     * @event ddsNotificationOpeningEvent
     */
    const buildNotification = () => {
        const notificationString = notificationTemplate(mergedProps);
        const notificationHTML = new DOMParser().parseFromString(notificationString, "text/html")
            .body.firstChild;

        const secondaryButton = notificationHTML.querySelector(
            `.${componentClasses.button.secondary}`
        );
        const primaryButton = notificationHTML.querySelector(`.${componentClasses.button.primary}`);
        const closeButton = notificationHTML.querySelector(`.${componentClasses.icon.close}`);

        if (mergedProps.closeIcon) {
            closeButton.insertAdjacentHTML("beforeend", icons.close());
        }

        secondaryButton &&
            secondaryButton.addEventListener("click", () => {
                mergedProps.secondaryAction();
                hide();
            });

        primaryButton &&
            primaryButton.addEventListener("click", () => {
                mergedProps.primaryAction();
                hide();
            });

        closeButton &&
            closeButton.addEventListener("click", () => {
                hide();
            });

        notificationElement = notificationWrapper.appendChild(notificationHTML);
        setTimeout(() => {
            renderState(views.show, notificationElement);
            dispatchEvent(eventMap[actions.show().type], notificationElement);
        }, 0);
    };

    /**
     * Hide the notification
     * @public
     * @event ddsNotificationClosingEvent
     */
    const hide = () => {
        renderState(views.hide, notificationElement);
        dispatchEvent(eventMap[actions.hide().type], notificationElement);
        notificationElement.addEventListener("transitionend", dispose);
    };

    /**
     * Dispose - Removes the element from the DOM
     * and clears its reference.
     * @public
     */
    const dispose = () => {
        if (notificationElement) notificationWrapper.removeChild(notificationElement);
        notificationElement = null;
    };

    let notificationElement;
    let notificationWrapper;

    notificationWrapper = element.querySelector(`[data-dds="notification"]`);
    if (!notificationWrapper) buildWrapper();

    buildNotification();

    const api = {
        element: notificationElement,
        dispose,
        hide,
    };

    // Final assignment
    notificationElement.api = api;
    return api;
}
